import auth0 from "auth0-js";

export const AUTHORIZATION_HEADER_KEY = "Authorization";
export const ACCESS_TOKEN_KEY = "access_token";
export const EXPIRES_AT_KEY = "expires_in";
/** Renew tokens 5 minutes before expiry to prevent race conditions with background-tab timer throttling */
export const RENEWAL_BUFFER_MS = 5 * 60 * 1000;
/** Delay before retrying a renewal that failed with a transient (non-terminal) error */
export const RENEWAL_RETRY_MS = 30 * 1000;
/** Auth0 checkSession error codes meaning the SSO session is actually gone (as opposed to a transient network/timeout hiccup) */
const TERMINAL_AUTH_ERRORS = ["login_required", "consent_required", "interaction_required"];

export type AuthServiceType = {
  isAuthenticated: () => boolean;
  getAccessToken: () => string;
  handleAuthentication: () => Promise<string | any>;
  setSession: (authResult: any) => void;
  refreshSession: () => Promise<void>;
  isSessionLost: (refreshError: any) => boolean;
  scheduleRenewal: () => void;
  renewToken: () => Promise<void>;
  logout: (callback?: () => void) => void;
  signInWithEmail: () => Promise<void>;
  checkCallbackForAuthInfo: (location: { hash: any }) => Promise<any>;
  getUserInfo: (accessToken: string) => Promise<any>;
};

export class AuthService {
  tokenRenewalTimeout: ReturnType<typeof setTimeout> | null = null;
  private refreshInFlight: Promise<void> | null = null;

  auth0 = new auth0.WebAuth({
    domain: import.meta.env.VITE_AUTH0_DOMAIN as string,
    clientID: import.meta.env.VITE_AUTH0_CLIENT_ID as string,
    redirectUri: `${window.location.origin}/callback`,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE as string,
    responseType: "token id_token",
    scope:
      "openid create:keyrequests create:invites onboard:objects onboard:screens",
  });

  constructor() {
    this.setupVisibilityHandler();
    this.scheduleRenewal();
  }

  /** Stores tokens in localStorage and reschedules renewal. Single source of truth for token persistence. */
  setSession = (authResult: any): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, authResult.accessToken);
    if (authResult.expiresIn) {
      const expiresAt = JSON.stringify(
        authResult.expiresIn * 1000 + new Date().getTime(),
      );
      localStorage.setItem(EXPIRES_AT_KEY, expiresAt);
    }
    this.scheduleRenewal();
  };

  /** Promise-based silent refresh that stores the new session. Concurrent callers share one checkSession call. */
  refreshSession = (): Promise<void> => {
    if (!this.refreshInFlight) {
      this.refreshInFlight = new Promise<void>((resolve, reject) => {
        this.auth0.checkSession({}, (err: any, authResult: any) => {
          if (err) return reject(err);
          this.setSession(authResult);
          resolve();
        });
      }).finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  };

  /**
   * Whether a refreshSession() failure means the user must log in again: a terminal
   * Auth0 error, or any error once the current token has expired. Transient errors
   * (timeout, network) with a still-valid token are recoverable.
   */
  isSessionLost = (refreshError: any): boolean =>
    TERMINAL_AUTH_ERRORS.includes(refreshError?.error) || !this.isAuthenticated();

  /** Current access token from localStorage. Always read at call time so renewals are picked up immediately. */
  getAccessToken = (): string => localStorage.getItem(ACCESS_TOKEN_KEY) ?? "";

  isAuthenticated = (): boolean => {
    const expiresAt = this.parseExpiry();
    if (expiresAt === null) return false;
    return Date.now() < expiresAt;
  };

  handleAuthentication(): Promise<string | any> {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash(async (err, authResult) => {
        if (err) return reject(err);
        if (authResult && authResult.accessToken && authResult.idToken) {
          this.setSession(authResult);
          resolve(true);
        }
      });
    });
  }

  scheduleRenewal(): void {
    // Clear any previously scheduled renewal to prevent timer stacking
    if (this.tokenRenewalTimeout) {
      clearTimeout(this.tokenRenewalTimeout);
      this.tokenRenewalTimeout = null;
    }

    const expiresAt = this.parseExpiry();
    if (expiresAt === null) return;

    const delay = Math.max(0, expiresAt - Date.now() - RENEWAL_BUFFER_MS);

    this.tokenRenewalTimeout = setTimeout(() => {
      this.renewToken();
    }, delay);
  }

  renewToken(): Promise<void> {
    return this.refreshSession().catch((err: any) => {
      console.warn("Auth0 token renewal failed", err);
      // Transient errors leave a still-valid token in place, so retry shortly instead
      if (this.isSessionLost(err)) {
        this.logout();
        return;
      }
      this.scheduleRetry();
    });
  }

  private scheduleRetry(): void {
    if (this.tokenRenewalTimeout) {
      clearTimeout(this.tokenRenewalTimeout);
    }
    this.tokenRenewalTimeout = setTimeout(() => {
      this.renewToken();
    }, RENEWAL_RETRY_MS);
  }

  logout = (callback?: () => void): void => {
    if (this.tokenRenewalTimeout) {
      clearTimeout(this.tokenRenewalTimeout);
      this.tokenRenewalTimeout = null;
    }
    localStorage.clear();
    this.auth0.logout({ returnTo: window.location.origin });
    if (callback) {
      callback();
    }
  };

  signInWithEmail = async (): Promise<void> => {
    const data = await this.auth0.authorize({
      connection: "Username-Password-Authentication",
    });
    console.log("logged-in", data);
  };

  checkCallbackForAuthInfo = (location: { hash: any }): Promise<any> => {
    if (/access_token|id_token|error/.test(location.hash)) {
      return new Promise((resolve, reject) => {
        this.auth0.parseHash((err, authResult) => {
          if (authResult && authResult.accessToken && authResult.idToken) {
            resolve(authResult);
          } else if (err) {
            reject(err);
          } else {
            reject(new Error("No auth result found"));
          }
        });
      });
    }
    return Promise.reject(new Error("No auth info in callback"));
  };

  getUserInfo = (accessToken: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      this.auth0.client.userInfo(accessToken, (err: any, user: any) => {
        if (user) {
          resolve(user);
        } else if (err) {
          reject(err);
        }
      });
    });
  };

  /** Safely parses the expiry epoch from localStorage. Returns null on missing, malformed JSON, or non-finite value. */
  private parseExpiry(): number | null {
    const raw = localStorage.getItem(EXPIRES_AT_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  /** Proactively renews the token when the user returns to the tab after a long absence */
  private setupVisibilityHandler(): void {
    if (typeof document === "undefined") return;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return;
      const expiresAt = this.parseExpiry();
      if (expiresAt === null) return;
      if (Date.now() >= expiresAt - RENEWAL_BUFFER_MS) {
        this.renewToken();
      }
    });
  }
}

const auth: AuthServiceType = new AuthService();

export default auth;
